import React, {
    useState,
    useEffect,
    useRef,
    ForwardedRef,
    useCallback,
    useMemo,
    memo,
} from 'react'
import { Typography, Tooltip, TypographyProps } from '@mui/material'

interface TruncatedTextProps extends TypographyProps {
    text?: string | null
    auto?: boolean
    children?: React.ReactNode
    tooltipTitle?: string
}

const TruncatedText = React.forwardRef<HTMLDivElement, TruncatedTextProps>(
    (
        { children, text, auto = false, tooltipTitle, ...props },
        ref: ForwardedRef<HTMLDivElement>,
    ) => {
        const [hasTooltip, setHasTooltip] = useState(false)
        const textRef = useRef<HTMLDivElement | null>(null)

        useEffect(() => {
            const el = textRef.current
            if (el) {
                setHasTooltip(el.scrollWidth > el.clientWidth)
            }
        }, [text, children])

        const content = useMemo(() => text ?? children ?? '', [text, children])

        const setRefs = useCallback(
            (node: HTMLDivElement | null) => {
                textRef.current = node
                if (typeof ref === 'function') {
                    ref(node)
                } else if (ref) {
                    // Safely assign to mutable ref
                    ;(
                        ref as React.MutableRefObject<HTMLDivElement | null>
                    ).current = node
                }
            },
            [ref],
        )

        const commonSx = useMemo(
            () => ({
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                ...props.sx,
            }),
            [props.sx],
        )

        return hasTooltip || auto ? (
            <Tooltip
                title={tooltipTitle || String(content)}
                placement="top"
                arrow
            >
                <Typography
                    {...props}
                    ref={setRefs}
                    component="div"
                    sx={commonSx}
                >
                    {content}
                </Typography>
            </Tooltip>
        ) : (
            <Typography {...props} ref={setRefs} component="div" sx={commonSx}>
                {content}
            </Typography>
        )
    },
)

TruncatedText.displayName = 'TruncatedText'

const MemoizedTruncatedText = memo(TruncatedText)

export default MemoizedTruncatedText
